import { Table } from "antd";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { DateRangePicker } from "rsuite";
import { PaginationInput, SelfTerminalPaginationInput } from "src/api/types";
import SearchFilter from "src/components/search-filters/basic-search-filter";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { useDateTimePicker } from "src/lib/hooks/useDateTimePicker";
import {
  currentTimeForDataPicker,
  fixAfterDecimal,
  getCurrencySymbol,
  getErrorMessage,
} from "src/lib/utils";
import client from "src/api/index";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import moment from "moment";
import { DateRange } from "rsuite/esm/DateRangePicker/types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import debounce from "lodash/debounce";

const SelfTerminalTransactions = () => {
  const isMounted = useIsMounted();
  const [searchText, setSearchText] = useState("");
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  const { canView, canList } = usePermissions(
    permissionConstants.SELF_TERMINAL
  );
  const canViewTransactions = canView || canList;

  const [total, setTotal] = useState(0);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [dateRangeValue, setDateRangeValue] = useState<DateRange | null>(null);
  const [timeRangeValue, setTimeRangeValue] = useState<DateRange | null>(null);

  const { afterToday } = DateRangePicker;

  const now = new Date();
  const prevMonth = new Date(now.setMonth(now.getMonth() - 1));

  const [startDate, endDate] = dateRangeValue || [];
  const [startTime, endTime] = timeRangeValue || [];

  dayjs.extend(utc);
  dayjs.extend(timezone);
  const tz = dayjs.tz.guess();

  const formatDateTime = (
    date: Date | undefined,
    time: Date | undefined
  ): string | undefined => {
    if (date && time) {
      const combined = `${moment(date).format("YYYY-MM-DD")} ${moment(
        time
      ).format("HH:mm")}`;
      let localTime = moment(combined, "YYYY-MM-DD HH:mm");
      const utcTime = localTime.utc();
      return utcTime.format("YYYY-MM-DD HH:mm");
    } else if (date && !time) {
      return moment(date).format("YYYY-MM-DD");
    } else if (!date && time) {
      const today = moment().format("YYYY-MM-DD");
      const localTime = moment(
        `${today} ${moment(time).format("HH:mm")}`,
        "YYYY-MM-DD HH:mm"
      );
      const utcTime = localTime.utc();

      return utcTime.format("YYYY-MM-DD HH:mm");
    }

    return undefined;
  };

  useEffect(() => {
    const start = formatDateTime(startDate, startTime);
    setSearchParams((prev) => ({
      ...prev,
      current_page: 1, // reset pagination when filter changes
      start_date: start,
    }));
  }, [startDate, startTime]);

  useEffect(() => {
    const end = formatDateTime(endDate, endTime);
    setSearchParams((prev) => ({
      ...prev,
      current_page: 1,
      end_date: end,
    }));
  }, [endDate, endTime]);

  const { mutate: getList, isLoading } = useMutation(
    client.selfTerminal.getAllTransactions,
    {
      onSuccess: (data: any) => {
        const obj = data.data;
        setTotal(obj.total);

        setDataSource(
          obj.data.map((item: any) => ({
            key: item.id,
            id: item.id,
            name: `${item.user.user_data.first_name}   ${item.user.user_data.last_name}`,
            currencySymbol: getCurrencySymbol(item.asset.code),
            providerName: item.provider?.provider_name,
            amount: item.amount,
            createdAt: item.createdAt,
            ...item,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Id
        </p>
      ),
      dataIndex: "id",
      key: "id",
      className: "w-auto",
      editable: false,
      render: (value: string) => {
        return (
          <div className="flex flex-row gap-2 items-center">
            <p className="font-manrope text-primary text-[14px] font-medium">
              {value}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Name
        </p>
      ),
      dataIndex: "name",
      key: "name",
      className: "w-auto",
      editable: false,
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value}{" "}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Amount{" "}
        </p>
      ),
      dataIndex: "amount",
      key: "amount",
      className: "w-auto",
      editable: true,
      render: (value: number, variables: any) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {variables.currencySymbol} {fixAfterDecimal(value, 2)}{" "}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Provider Name
        </p>
      ),
      dataIndex: "providerName",
      key: "providerName",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Created Date
        </p>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
        </p>
      ),
    },
  ];

  const [searchParams, setSearchParams] = useState<SelfTerminalPaginationInput>(
    {
      per_page: LIST_PAGE_SIZE,
      current_page: 1,
      start_date: startDate,
      end_date: endDate,
      provider_name: "",
      timezone: "",
    }
  );

  const onPageChange = (page: number) => {
    setSearchParams((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    const tz = dayjs.tz.guess();
    getList({ ...searchParams, timezone: tz });
  }, [
    isMounted,
    canViewTransactions,
    searchParams.current_page,
    searchParams.end_date,
    searchParams.start_date,
  ]);

  const debounceSearch = useMemo(
    () =>
      debounce((searchText: string) => {
        setSearchParams((prev) => ({
          ...prev,
          current_page: 1, // reset ONLY on search
          provider_name: searchText,
        }));
      }, 500),
    []
  );

  useEffect(() => {
    debounceSearch(searchText);
  }, [searchText, debounceSearch]);

  // cancel on unmount
  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col">
        <div className="flex flex-col w-full p-2 sm:p-4">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Transactions
          </p>
          <div className="flex align-center gap-2">
            <div className="">
              <SearchFilter
                placeholder="Search by Provider Name"
                searchText={searchText}
                setSearchText={setSearchText}
              />
            </div>
            <div className="flex gap-2">
              <DateRangePicker
                format="dd/MM/yyyy"
                size="lg"
                block
                placement="bottomEnd"
                style={{ width: "308px" }}
                value={dateRangeValue}
                onChange={setDateRangeValue}
                defaultCalendarValue={[prevMonth, now]}
                shouldDisableDate={afterToday()}
                ranges={[]}
              />

              <DateRangePicker
                format="HH:mm"
                size="lg"
                block
                placement="bottomEnd"
                style={{ width: "200px" }}
                value={timeRangeValue}
                onChange={setTimeRangeValue}
                defaultCalendarValue={[prevMonth, now]}
                ranges={[]}
              />
            </div>
          </div>
        </div>
        {!canViewTransactions ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] m-4 p-8 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view transactions.
            </p>
          </div>
        ) : (
          <Table
            loading={isLoading}
            showSorterTooltip={false}
            columns={columns}
            dataSource={dataSource}
            pagination={{
              total,
              current: searchParams.current_page,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
            rowClassName="editable-row"
          />
        )}
      </div>
    </div>
  );
};

export default SelfTerminalTransactions;
